// src/components/ClientDashboard.js
// UPDATED VERSION - Redesigned dashboard layout with collapsible notifications,
// dismissible notification items, and two-column Weekly Update card

import React, { useState, useEffect, useMemo } from "react";
import { Link } from "react-router-dom";
import { useAuthState } from "react-firebase-hooks/auth";
import { auth } from "../../firebase";
import { constructionPhaseSummaries } from "../../utils/constructionPhaseSummaries";
import { useClientProject } from "../../hooks";
import { useProjectUpdates } from "../../hooks/useUpdates";
import { useClientSelectionsData } from "../../hooks/useSelections";
import { useNotificationState } from "../../hooks/useNotificationState";
import { useChangeOrders } from "../../hooks/useChangeOrders";
import { PhaseIcon } from "../../components/icons/phaseicons";
import InfoModal from "../../components/InfoModal";
import "./ClientDashboard.css";
import { formatDateET } from "../../utils/dateUtils";

import {
  FaHome,
  FaHardHat,
  FaChartLine,
  FaCalendarCheck,
  FaClipboardList,
  FaBell,
  FaCheckCircle,
  FaClock,
  FaFileContract,
  FaExclamationTriangle,
  FaArrowRight,
  FaImage,
  FaFileAlt,
  FaBullhorn,
  FaPauseCircle,
} from "react-icons/fa";

import {
  FiCheckCircle,
  FiCamera,
  FiX,
  FiChevronLeft,
  FiChevronRight,
  FiChevronDown,
  FiChevronUp,
  FiFileText,
  FiLayers,
  FiPenTool,
} from "react-icons/fi";

/**
 * ClientDashboard Component
 * Main dashboard view for client portal showing project status,
 * progress, weekly updates, and notifications.
 *
 * @returns {JSX.Element} The dashboard component
 */
function ClientDashboard() {
  const [user] = useAuthState(auth);

  // State for notifications panel collapse (default: open on desktop, closed on mobile)
  const [notificationsExpanded, setNotificationsExpanded] = useState(
    window.innerWidth > 768,
  );

  // Firestore-based notification state management
  const {
    loading: notificationStateLoading,
    dismissNotification,
    isNotificationDismissed,
    getNewItemCount,
    updateLastVisited,
  } = useNotificationState(user?.uid);

  // State for Construction Phase modal
  const [showPhaseModal, setShowPhaseModal] = useState(false);

  // State for Weekly Updates task modal
  const [selectedDigestTask, setSelectedDigestTask] = useState(null);

  // State for Weekly Updates photo gallery
  const [digestGallery, setDigestGallery] = useState({
    isOpen: false,
    photos: [],
    currentIndex: 0,
  });

  // Get the project data
  const {
    data: response,
    isLoading: projectLoading,
    error: projectError,
  } = useClientProject();

  // Extract the actual project data from the response
  const project = response?.data || response;

  // Get updates data for weekly digest
  const { data: updatesResponse, isLoading: updatesLoading } =
    useProjectUpdates(project?.id);

  // Extract updates data
  const updatesData = updatesResponse?.data || updatesResponse;

  // Fetch selections/documents data
  const { data: selectionsResponse } = useClientSelectionsData(project?.id);
  const selectionsData = selectionsResponse?.data || selectionsResponse;

  // Get change orders data for notifications
  const { data: changeOrdersResponse } = useChangeOrders(project?.id);
  const changeOrdersData =
    changeOrdersResponse?.data || changeOrdersResponse || [];

  // Get latest 3 tasks for display
  const latestTasks = updatesData?.currentWeek?.tasks?.slice(0, 3) || [];

  // Count stats
  const weeklyTaskCount = updatesData?.currentWeek?.tasks?.length || 0;
  const weeklyPhotoCount =
    updatesData?.currentWeek?.photosCount ||
    updatesData?.currentWeek?.tasks?.reduce(
      (sum, task) => sum + (task.attachments?.length || 0),
      0,
    ) ||
    0;

  // Update last visited timestamp when component mounts
  useEffect(() => {
    if (user?.uid && !notificationStateLoading) {
      updateLastVisited();
    }
  }, [user?.uid, notificationStateLoading, updateLastVisited]);

  // Get ONLY current week's progress photos for the photo grid (max 10)
  const currentWeekPhotos = useMemo(() => {
    if (!updatesData) return [];

    const photos = [];

    // Get photos from current week tasks' attachments
    const currentWeekTasks = updatesData.currentWeek?.tasks || [];
    currentWeekTasks.forEach((task) => {
      if (task.attachments && task.attachments.length > 0) {
        task.attachments.forEach((attachment) => {
          photos.push({
            ...attachment,
            taskName: task.name,
            dateCompleted: task.dateCompleted,
          });
        });
      }
    });

    // Also check for standalone current week photos (if available)
    const weekPhotos = updatesData.currentWeek?.photos || [];
    weekPhotos.forEach((photo) => {
      // Avoid duplicates by checking URL
      if (!photos.some((p) => p.url === photo.url)) {
        photos.push(photo);
      }
    });

    // Sort by date (newest first) and limit to 10 for the 5x2 grid
    return photos
      .sort((a, b) => {
        const dateA = new Date(a.uploadDate || a.dateCompleted || 0);
        const dateB = new Date(b.uploadDate || b.dateCompleted || 0);
        return dateB - dateA;
      })
      .slice(0, 10);
  }, [updatesData]);

  // Toggle notifications panel
  const toggleNotifications = () => {
    setNotificationsExpanded((prev) => !prev);
  };

  // Helper functions for digest task modal
  const openDigestTaskModal = (task) => {
    setSelectedDigestTask(task);
  };

  const closeDigestTaskModal = () => {
    setSelectedDigestTask(null);
  };

  // Helper functions for photo gallery
  const openPhotoGallery = (photos, startIndex = 0) => {
    setDigestGallery({
      isOpen: true,
      photos,
      currentIndex: startIndex,
    });
  };

  const closePhotoGallery = () => {
    setDigestGallery({
      isOpen: false,
      photos: [],
      currentIndex: 0,
    });
  };

  const navigatePhotoGallery = (direction) => {
    setDigestGallery((prev) => {
      const newIndex =
        direction === "next"
          ? (prev.currentIndex + 1) % prev.photos.length
          : (prev.currentIndex - 1 + prev.photos.length) % prev.photos.length;
      return { ...prev, currentIndex: newIndex };
    });
  };

  // Handle photo click from grid
  const handlePhotoClick = (index) => {
    openPhotoGallery(currentWeekPhotos, index);
  };

  // Loading state
  if (projectLoading) {
    return (
      <div className="component-loading">
        <div className="component-spinner"></div>
        <p>Retrieving your project information...</p>
      </div>
    );
  }

  // Error state
  if (projectError) {
    return (
      <div className="dashboard-error">
        <FaExclamationTriangle />
        <h2>Unable to Load Dashboard</h2>
        <p>{projectError.message}</p>
        <button onClick={() => window.location.reload()}>Try Again</button>
      </div>
    );
  }

  // No project found
  if (!project || !project.id) {
    return (
      <div className="dashboard-no-project">
        <FaHome className="icon-large" />
        <h2>Welcome to My Red Setter Home</h2>
        <p>No active project found for your account.</p>
        <p>Please contact your project manager for assistance.</p>
      </div>
    );
  }

  // Format date helper (local version for notifications)
  const formatDateLocal = (dateString) => {
    if (!dateString) return "TBD";

    try {
      const dateWithTime = dateString.includes("T")
        ? dateString
        : `${dateString}T12:00:00`;
      const date = new Date(dateWithTime);
      return new Intl.DateTimeFormat("en-US", {
        timeZone: "America/New_York",
        month: "short",
        day: "numeric",
        year: "numeric",
      }).format(date);
    } catch (error) {
      console.error("Error formatting date:", error);
      return "Invalid Date";
    }
  };

  // Get days until date helper
  const getDaysUntilLocal = (dateString) => {
    if (!dateString) return null;

    const todayET = new Date(
      new Date().toLocaleString("en-US", { timeZone: "America/New_York" }),
    );
    todayET.setHours(0, 0, 0, 0);

    const targetString = dateString.includes("T")
      ? dateString
      : `${dateString}T12:00:00`;
    const targetDate = new Date(targetString);
    targetDate.setHours(0, 0, 0, 0);

    return Math.ceil((targetDate - todayET) / (1000 * 60 * 60 * 24));
  };

  // Get construction phase icon
  /* const getPhaseIcon = (phase) => {
    const phaseIcons = {
      "In Development": <InDevelopmentIcon />,
      "Planning/Permitting": "📋",
      Foundation: "🏗️",
      Framing: "🔨",
      "Rough-In": "🔧",
      "Insulation & Drywall": <InsulationDrywallIcon />,
      "Trim & Paint": "🎨",
      "Mechanical Trim": "⚡",
      "Final Finishes": "✨",
      "Punch List": "📝",
      "Final Inspection / CO": "📜",
      Complete: "✅",
    };
    return phaseIcons[phase] || "🏠";
  };*/

  // =========================
  // NOTIFICATION SYSTEM
  // =========================

  /**
   * Check for change order notifications
   * Returns notifications for new change orders and status changes
   *
   * @param {Array} changeOrders - Array of change order objects
   * @returns {Array} Array of notification objects
   */
  const getChangeOrderNotifications = (changeOrders) => {
    if (
      !changeOrders ||
      !Array.isArray(changeOrders) ||
      changeOrders.length === 0
    ) {
      return [];
    }

    const notifications = [];

    changeOrders.forEach((co) => {
      // Notification for new change order (when it was submitted)
      if (co.dateSubmitted) {
        const statusLabel = co.status?.toLowerCase() || "submitted";
        const statusType =
          statusLabel === "approved" || statusLabel === "completed"
            ? "success"
            : statusLabel === "denied"
              ? "error"
              : statusLabel === "submitted"
                ? "warning"
                : "info";

        notifications.push({
          type: statusType,
          icon: <FaFileContract />,
          message: `Change Order [${co.name || `${co.coNumber}`} - ${
            co.title
          }] has been ${co.status || "Submitted"}`,
          timestamp: co.statusChangeDate || co.dateSubmitted,
          priority: co.status === "Submitted" ? 7 : 5,
          changeOrderId: co.id,
        });
      }
    });

    return notifications;
  };

  const getTodayFormatted = () => {
    return new Date().toLocaleDateString("en-US", {
      timeZone: "America/New_York",
      month: "short",
      day: "numeric",
      year: "numeric",
    });
  };

  const getStatusNotification = (proj) => {
    if (!proj?.status) return null;

    const statusDate = proj.projectStatusChangedDate || proj.startDate || null;

    switch (proj.status) {
      case "Active Construction":
        return {
          id: `status-${proj.status}`,
          type: "info",
          icon: <FaHardHat />,
          message: "Your home is now under construction.",
          timestamp: statusDate,
          priority: 6,
          dismissType: "permanent",
        };

      case "Construction Delayed":
        return {
          id: `status-${proj.status}`,
          type: "warning",
          icon: <FaPauseCircle />,
          message: "Your home's construction is currently delayed.",
          timestamp: statusDate,
          priority: 7,
          dismissType: "permanent",
        };

      case "Construction Completed / Under Warranty":
        return {
          id: `status-${proj.status}`,
          type: "success",
          icon: <FaCheckCircle />,
          message: "Congratulations! Your home construction is complete.",
          timestamp: proj.actualCompletionDate || statusDate,
          priority: 8,
          dismissType: "permanent",
        };

      case "Warranty Period Complete":
        return {
          id: `status-${proj.status}`,
          type: "success",
          icon: <FaCheckCircle />,
          message: "Your warranty period has been completed.",
          timestamp: statusDate,
          priority: 5,
          dismissType: "permanent",
        };

      case "Pre-Construction":
        return {
          id: `status-${proj.status}`,
          type: "info",
          icon: <FaHardHat />,
          message: "Your project is in the pre-construction phase.",
          timestamp: statusDate,
          priority: 4,
          dismissType: "permanent",
        };

      default:
        return null;
    }
  };

  const checkForProgressUpdates = (updates) => {
    if (!updates) return null;

    const currentWeekTasks = updates.currentWeek?.tasks || [];
    const previousWeeks = updates.previousWeeks || [];
    const previousTasks = previousWeeks.flatMap((week) => week.tasks || []);
    const allTasks = [...currentWeekTasks, ...previousTasks];

    if (allTasks.length === 0) return null;

    const sortedByDate = allTasks
      .filter((task) => task.dateCompleted)
      .sort((a, b) => new Date(a.dateCompleted) - new Date(b.dateCompleted));

    const firstTask = sortedByDate[0];
    if (!firstTask) return null;

    return {
      id: "first-progress-update",
      type: "success",
      icon: <FaBullhorn />,
      message: firstTask.dateCompleted
        ? `Your first progress update was posted on ${formatDateLocal(
            firstTask.dateCompleted,
          )}`
        : "Your first progress update has been posted!",
      timestamp: firstTask.dateCompleted,
      priority: 4,
      dismissType: "permanent",
    };
  };

  /**
   * Check for selection documents and calculate new vs total counts
   * @param {Object} selections - Selections data
   * @param {Function} getNewCount - Function to get new item count
   * @returns {Object|null} Notification or null
   */
  const checkForSelectionDocuments = (selections, getNewCount) => {
    if (!selections?.clientSelections?.documents) return null;

    const docs = selections.clientSelections.documents;
    const allDocuments = [
      ...(docs.roofingSiding || []),
      ...(docs.exteriorDoorWindow || []),
      ...(docs.garageDoor || []),
      ...(docs.fireplace || []),
      ...(docs.cabinets || []),
      ...(docs.countertopsSinks || []),
      ...(docs.showersTubs || []),
      ...(docs.plumbingFixtures || []),
      ...(docs.lightingFixtures || []),
      ...(docs.flooring || []),
    ];

    const totalCount = allDocuments.length;
    if (totalCount === 0) return null;

    const latestDate = selections.clientSelections.lastModified || null;

    // Calculate new documents since last dismissal
    const newCount = getNewCount
      ? getNewCount("documents", totalCount)
      : totalCount;

    // Only show notification if there are new documents
    if (newCount === 0) return null;

    // Use stable ID based on total count (changes when new docs added)
    return {
      id: `selection-docs-${totalCount}`,
      type: "info",
      icon: <FaFileAlt />,
      message:
        newCount === totalCount
          ? `${totalCount} selection document${
              totalCount > 1 ? "s" : ""
            } available`
          : `${newCount} new selection document${
              newCount > 1 ? "s" : ""
            } available`,
      timestamp: latestDate,
      priority: 3,
      dismissType: "permanent",
      totalDocumentCount: totalCount,
    };
  };

  /**
   * Check for progress photos and calculate new vs total counts
   * @param {Object} updates - Updates data
   * @param {Function} getNewCount - Function to get new item count
   * @returns {Object|null} Notification or null
   */
  const checkForProgressPhotos = (updates, getNewCount) => {
    if (!updates) return null;

    const currentWeekPhotos = updates.currentWeek?.photos || [];
    const photosByWeek = updates.photosByWeek || [];

    const allPhotos = [
      ...currentWeekPhotos,
      ...photosByWeek.flatMap((week) => week.photos || []),
    ];

    const uniquePhotos = Array.from(
      new Map(
        allPhotos.map((photo) => [photo.url || photo.id, photo]),
      ).values(),
    );

    const totalCount = uniquePhotos.length;
    if (totalCount === 0) return null;

    const sortedByDate = uniquePhotos
      .filter((photo) => photo.uploadDate)
      .sort((a, b) => new Date(b.uploadDate) - new Date(a.uploadDate));

    const latestPhoto = sortedByDate[0];

    // Calculate new photos since last dismissal
    const newCount = getNewCount
      ? getNewCount("photos", totalCount)
      : totalCount;

    // Only show notification if there are new photos
    if (newCount === 0) return null;

    return {
      id: `progress-photos-${totalCount}`,
      type: "info",
      icon: <FaImage />,
      message:
        newCount === totalCount
          ? `${totalCount} progress photo${
              totalCount > 1 ? "s" : ""
            } added to your project`
          : `${newCount} new progress photo${newCount > 1 ? "s" : ""} added`,
      timestamp: latestPhoto?.uploadDate || null,
      priority: 3,
      dismissType: "permanent",
      totalPhotoCount: totalCount,
    };
  };

  const generateNotifications = (proj, updates, selections, changeOrders) => {
    const notificationList = [];

    const parseDate = (dateStr) => {
      if (!dateStr) return new Date(0);
      try {
        return new Date(
          dateStr.includes("T") ? dateStr : `${dateStr}T12:00:00`,
        );
      } catch {
        return new Date(0);
      }
    };

    const todayFormatted = getTodayFormatted();
    const todayISO = new Date().toISOString();

    // 1. WELCOME MESSAGE
    notificationList.push({
      id: "welcome",
      type: "success",
      icon: <FaCheckCircle />,
      message: "Welcome to your project portal!",
      timestamp: null,
      displayDate: null,
      priority: 0,
      dismissType: "permanent",
    });

    // 2. CONSTRUCTION STATUS
    const statusNotification = getStatusNotification(proj);
    if (statusNotification) {
      notificationList.push(statusNotification);
    }

    // 3. SELECTIONS DUE DATE
    if (proj?.selectionsDueDate) {
      const daysUntilDue = getDaysUntilLocal(proj.selectionsDueDate);

      if (daysUntilDue === 0) {
        notificationList.push({
          id: "selections-due-today",
          type: "error",
          icon: <FaClock />,
          message: "Selections are due TODAY!",
          timestamp: todayISO,
          displayDate: todayFormatted,
          priority: 10,
          dismissType: "session",
        });
      } else if (
        daysUntilDue !== null &&
        daysUntilDue > 0 &&
        daysUntilDue <= 45
      ) {
        notificationList.push({
          id: "selections-due-soon",
          type: "warning",
          icon: <FaClock />,
          message: `Selections due in ${daysUntilDue} day${
            daysUntilDue > 1 ? "s" : ""
          }`,
          timestamp: todayISO,
          displayDate: todayFormatted,
          priority: 5,
          dismissType: "session",
        });
      } else if (daysUntilDue !== null && daysUntilDue < 0) {
        const daysPassed = Math.abs(daysUntilDue);
        notificationList.push({
          id: "selections-past-due",
          type: "warning",
          icon: <FaClock />,
          message: `Selections were finalized ${daysPassed} day${
            daysPassed > 1 ? "s" : ""
          } ago. Please submit a change order request to make changes.`,
          timestamp: todayISO,
          displayDate: todayFormatted,
          priority: 3,
          dismissType: "session",
        });
      }
    }

    // 4. FIRST PROGRESS UPDATE
    const progressNotification = checkForProgressUpdates(updates);
    if (progressNotification) {
      notificationList.push(progressNotification);
    }

    // 5. SELECTION DOCUMENTS (pass getNewItemCount for "new" calculation)
    const selectionDocsNotification = checkForSelectionDocuments(
      selections,
      getNewItemCount,
    );
    if (selectionDocsNotification) {
      notificationList.push(selectionDocsNotification);
    }

    // 6. PROGRESS PHOTOS (pass getNewItemCount for "new" calculation)
    const progressPhotosNotification = checkForProgressPhotos(
      updates,
      getNewItemCount,
    );
    if (progressPhotosNotification) {
      notificationList.push(progressPhotosNotification);
    }
    // 7. CHANGE ORDER NOTIFICATIONS
    const changeOrderNotifications = getChangeOrderNotifications(changeOrders);
    if (changeOrderNotifications.length > 0) {
      notificationList.push(...changeOrderNotifications);
    }
    // Sort by timestamp then priority
    return notificationList.sort((a, b) => {
      if (a.timestamp && b.timestamp) {
        const dateA = parseDate(a.timestamp);
        const dateB = parseDate(b.timestamp);
        if (dateB.getTime() !== dateA.getTime()) {
          return dateB - dateA;
        }
        return (b.priority || 0) - (a.priority || 0);
      }
      if (a.timestamp && !b.timestamp) return -1;
      if (!a.timestamp && b.timestamp) return 1;
      return (b.priority || 0) - (a.priority || 0);
    });
  };

  // Generate notifications
  const allNotifications = generateNotifications(
    project,
    updatesData,
    selectionsData,
    changeOrdersData,
  );

  // Filter out dismissed notifications
  const notifications = allNotifications.filter(
    (notification) => !isNotificationDismissed(notification.id),
  );

  // Convert progress to percentage
  const progressPercentage = Math.round((project.progress || 0) * 100);

  return (
    <>
      <div className="client-dashboard">
        <div className="page-container">
          <div className="page-header">
            <h1 className="page-title">Welcome to Your Project Dashboard</h1>
            <p className="page-subtitle">Your home is taking shape</p>
          </div>

          {/* Notification Center - Full Width at Top */}
          <div className="dashboard-notification-center">
            <div
              className="notification-center-header"
              onClick={toggleNotifications}
            >
              <div className="notification-center-title">
                <FaBell className="notification-center-icon" />
                <h2>Notification Center</h2>
                {notifications.length > 0 && (
                  <span className="notification-count-badge">
                    {notifications.length}
                  </span>
                )}
              </div>
              <button
                className="notification-toggle-btn"
                aria-label={
                  notificationsExpanded
                    ? "Collapse notifications"
                    : "Expand notifications"
                }
              >
                {notificationsExpanded ? <FiChevronUp /> : <FiChevronDown />}
              </button>
            </div>

            {notificationsExpanded && (
              <div className="notification-center-body">
                {notifications.length > 0 ? (
                  <div className="notifications-list">
                    {notifications.map((notification) => (
                      <div
                        key={notification.id}
                        className={`notification-item notification-${notification.type}`}
                      >
                        <span className="notification-icon">
                          {notification.icon}
                        </span>
                        <div className="notification-content">
                          <span className="notification-message">
                            {notification.message}
                          </span>
                          {(notification.displayDate ||
                            notification.timestamp) && (
                            <span className="notification-date">
                              {notification.displayDate ||
                                formatDateLocal(notification.timestamp)}
                            </span>
                          )}
                        </div>
                        <button
                          className="notification-dismiss-btn"
                          onClick={(e) => {
                            e.stopPropagation();
                            dismissNotification(notification.id, {
                              dismissType: notification.dismissType,
                              photoCount: notification.totalPhotoCount,
                              documentCount: notification.totalDocumentCount,
                            });
                          }}
                          aria-label="Dismiss notification"
                        >
                          <FiX />
                        </button>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="no-notifications">
                    <FaBell />
                    <p>No new notifications</p>
                    <p>You're all caught up!</p>
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Dashboard Grid */}
          <div className="dashboard-grid">
            {/* Row 1: Project Status | Construction Phase | Project Progress */}
            <div className="dashboard-card card-project-status">
              <div className="card-header">
                <FaHome className="card-icon" />
                <h2>Project Status</h2>
              </div>
              <div className="card-body">
                <h3 className="project-name">
                  {project.projectName || "Your Home"}
                </h3>
                <div
                  className={`status-badge status-${(
                    project.status || "Status Unknown"
                  )
                    .toLowerCase()
                    .replace(/\s+/g, "-")
                    .replace(/\//g, "-")}`}
                >
                  {project.status || "Active"}
                </div>
                <div className="project-address">
                  <p>{project.address || "Address to be confirmed"}</p>
                </div>
              </div>
            </div>
            <div className="dashboard-card card-construction-phase">
              <div className="card-header">
                <FaHardHat className="card-icon" />
                <h2>Construction Phase</h2>
              </div>
              <div className="card-body">
                <div className="phase-display">
                  <div className="phase-info">
                    <div className="phase-icon">
                      <PhaseIcon phase={project.phase} size={36} />
                    </div>
                    <div className="phase-details">
                      <h3>{project.phase || "Still on the drawing board"}</h3>
                    </div>
                  </div>
                </div>

                {constructionPhaseSummaries[project.phase] && (
                  <>
                    <div className="phase-divider"></div>
                    <div className="phase-summary">
                      <h4 className="summary-title">What's Happening Now</h4>
                      <p className="summary-description">
                        {
                          constructionPhaseSummaries[project.phase]
                            .shortDescription
                        }
                      </p>
                      <button
                        className="learn-more-button"
                        onClick={() => setShowPhaseModal(true)}
                      >
                        Learn More About This Phase
                      </button>
                    </div>
                  </>
                )}
              </div>
            </div>

            <div className="dashboard-card card-project-progress">
              <div className="card-header">
                <FaChartLine className="card-icon" />
                <h2>Project Progress</h2>
              </div>
              <div className="card-body">
                <div className="progress-container">
                  <div className="progress-header">
                    <span className="progress-label">Overall Completion</span>
                    <span className="progress-percentage">
                      {progressPercentage}%
                    </span>
                  </div>
                  <div className="progress-bar">
                    <div
                      className="progress-fill"
                      style={{ width: `${progressPercentage}%` }}
                    ></div>
                  </div>
                </div>
                <div className="progress-description">
                  <p>Your home is {progressPercentage}% complete</p>
                </div>
              </div>
            </div>

            {/* Row 2: Project Details | Weekly Update (spans 2 columns) */}
            <div className="dashboard-card card-project-details">
              <div className="card-header">
                <FaClipboardList className="card-icon" />
                <h2>Project Details</h2>
              </div>
              <div className="card-body">
                <div className="details-grid">
                  <div className="detail-row">
                    <span className="detail-label">Project Manager</span>
                    <span className="detail-value">
                      {project.projectManager || "Red Setter Team"}
                    </span>
                  </div>
                  <div className="detail-row">
                    <span className="detail-label">Start Date</span>
                    <span className="detail-value">
                      {formatDateET(project.startDate)}
                    </span>
                  </div>
                  <div className="detail-row">
                    <span className="detail-label">Est. Completion</span>
                    <span className="detail-value">
                      {formatDateET(project.completionDate)}
                    </span>
                  </div>
                  <div className="detail-row">
                    <span className="detail-label">Selections Due</span>
                    <span className="detail-value">
                      {formatDateET(project.selectionsDueDate)}
                    </span>
                  </div>
                </div>
              </div>
            </div>
            {/* Weekly Update Card  */}
            <div className="dashboard-card card-weekly-update">
              <div className="card-header">
                <FaCalendarCheck className="card-icon" />
                <h2>Weekly Update</h2>
                {/* Stats badges in header */}
                <div className="weekly-update-stats">
                  <span className="weekly-stat-badge">
                    <FiCheckCircle className="stat-icon completed" />
                    <span className="stat-value">{weeklyTaskCount}</span>
                    <span className="stat-label">
                      Task{weeklyTaskCount !== 1 ? "s" : ""}{" "}
                      <span className="hide-sm">Completed</span>
                    </span>
                  </span>
                  <span className="weekly-stat-badge">
                    <FiCamera className="stat-icon photos" />
                    <span className="stat-value">{weeklyPhotoCount}</span>
                    <span className="stat-label">
                      Photo{weeklyPhotoCount !== 1 ? "s" : ""}{" "}
                      <span className="hide-sm">Added</span>
                    </span>
                  </span>
                </div>
              </div>

              <div className="card-body weekly-update-body">
                {updatesLoading ? (
                  <div className="digest-loading">
                    <div className="spinner spinner-small"></div>
                    <span>Loading updates...</span>
                  </div>
                ) : weeklyTaskCount > 0 || currentWeekPhotos.length > 0 ? (
                  <div className="weekly-update-content">
                    {/* Left Column: Tasks */}
                    <div className="weekly-update-tasks">
                      <h4 className="weekly-section-title">
                        Latest Completed Tasks
                      </h4>
                      {latestTasks.length > 0 ? (
                        <ul className="digest-task-list">
                          {latestTasks.map((task) => (
                            <li
                              key={task.id}
                              className="digest-task-item"
                              onClick={() => openDigestTaskModal(task)}
                            >
                              <FiCheckCircle className="digest-task-check" />
                              <div className="digest-task-content">
                                <span className="digest-task-name">
                                  {task.name}
                                </span>
                                <span className="digest-task-date">
                                  {formatDateET(task.dateCompleted)}
                                </span>
                              </div>
                              {task.attachments &&
                                task.attachments.length > 0 && (
                                  <span className="digest-task-photos-badge">
                                    <FiCamera /> {task.attachments.length}
                                  </span>
                                )}
                            </li>
                          ))}
                        </ul>
                      ) : (
                        <p className="no-tasks-message">
                          No tasks completed this week
                        </p>
                      )}
                      <Link to="/updates" className="digest-view-all-link">
                        View All Task Updates <FaArrowRight />
                      </Link>
                    </div>

                    {/* Right Column: Progress Photos */}
                    <div className="weekly-update-photos">
                      <h4 className="weekly-section-title">Progress Photos</h4>
                      {currentWeekPhotos.length > 0 ? (
                        <div className="weekly-photo-grid">
                          {currentWeekPhotos.map((photo, index) => (
                            <div
                              key={photo.id || photo.url || index}
                              className="weekly-photo-item"
                              onClick={() => handlePhotoClick(index)}
                            >
                              <img
                                src={photo.thumbnailUrl || photo.url}
                                alt={
                                  photo.filename ||
                                  `Progress photo ${index + 1}`
                                }
                              />
                            </div>
                          ))}
                        </div>
                      ) : (
                        <p className="no-photos-message">
                          No progress photos yet
                        </p>
                      )}
                      <Link
                        to="/updates#progress-photos"
                        className="digest-view-all-link"
                      >
                        View All Progress Photos <FaArrowRight />
                      </Link>
                    </div>
                  </div>
                ) : (
                  <div className="digest-empty">
                    <FaCalendarCheck className="digest-empty-icon" />
                    <p>Weekly updates will appear here</p>
                    <p className="digest-empty-sub">
                      Check back soon for progress updates!
                    </p>
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Phase Details Modal */}
          <InfoModal
            isOpen={
              showPhaseModal && !!constructionPhaseSummaries[project.phase]
            }
            onClose={() => setShowPhaseModal(false)}
            title={project.phase}
            icon={<PhaseIcon phase={project.phase} />}
          >
            <p className="phase-full-summary">
              {constructionPhaseSummaries[project.phase]?.fullSummary}
            </p>
          </InfoModal>

          {/* Digest Task Detail Modal */}
          {selectedDigestTask && (
            <div className="modal-overlay" onClick={closeDigestTaskModal}>
              <div
                className="modal-container modal-sm digest-task-modal"
                onClick={(e) => e.stopPropagation()}
              >
                <button
                  className="modal-close"
                  onClick={closeDigestTaskModal}
                  aria-label="Close modal"
                >
                  <FiX />
                </button>

                <div className="digest-task-modal-header">
                  <FiCheckCircle className="digest-task-modal-icon" />
                  <h3>{selectedDigestTask.name}</h3>
                </div>

                <div className="digest-task-modal-body">
                  <div className="digest-task-modal-meta">
                    {selectedDigestTask.constructionPhase && (
                      <span className="digest-meta-badge phase">
                        <FiLayers /> {selectedDigestTask.constructionPhase}
                      </span>
                    )}
                    <span className="digest-meta-badge date">
                      <FiCheckCircle /> Completed{" "}
                      {formatDateET(selectedDigestTask.dateCompleted)}
                    </span>
                  </div>

                  {selectedDigestTask.description && (
                    <div className="digest-task-modal-section">
                      <h4>
                        <FiFileText /> Task Description
                      </h4>
                      <p className="digest-task-modal-text">
                        {selectedDigestTask.description}
                      </p>
                    </div>
                  )}

                  {selectedDigestTask.notes && (
                    <div className="digest-task-modal-section">
                      <h4>
                        <FiPenTool /> Project Manager's Notes
                      </h4>
                      <p className="digest-task-modal-text">
                        {selectedDigestTask.notes}
                      </p>
                    </div>
                  )}

                  {selectedDigestTask.attachments &&
                    selectedDigestTask.attachments.length > 0 && (
                      <div className="digest-task-modal-section">
                        <h4>
                          <FiCamera /> Photos (
                          {selectedDigestTask.attachments.length})
                        </h4>
                        <div className="digest-task-modal-photos">
                          {selectedDigestTask.attachments.map(
                            (attachment, idx) => (
                              <div
                                key={attachment.id || idx}
                                className="digest-task-modal-photo"
                                onClick={() =>
                                  openPhotoGallery(
                                    selectedDigestTask.attachments,
                                    idx,
                                  )
                                }
                              >
                                <img
                                  src={attachment.thumbnail || attachment.url}
                                  alt={attachment.filename || "Task photo"}
                                />
                              </div>
                            ),
                          )}
                        </div>
                      </div>
                    )}
                </div>
              </div>
            </div>
          )}

          {/* Photo Gallery Modal */}
          {digestGallery.isOpen && digestGallery.photos.length > 0 && (
            <div className="modal-overlay" onClick={closePhotoGallery}>
              <div
                className="digest-gallery"
                onClick={(e) => e.stopPropagation()}
              >
                <button
                  className="modal-close"
                  onClick={closePhotoGallery}
                  aria-label="Close gallery"
                >
                  <FiX />
                </button>

                {digestGallery.photos.length > 1 && (
                  <>
                    <button
                      className="digest-gallery-nav digest-gallery-prev"
                      onClick={(e) => {
                        e.stopPropagation();
                        navigatePhotoGallery("prev");
                      }}
                      aria-label="Previous photo"
                    >
                      <FiChevronLeft />
                    </button>
                    <button
                      className="digest-gallery-nav digest-gallery-next"
                      onClick={(e) => {
                        e.stopPropagation();
                        navigatePhotoGallery("next");
                      }}
                      aria-label="Next photo"
                    >
                      <FiChevronRight />
                    </button>
                  </>
                )}

                <div className="digest-gallery-image-container">
                  <img
                    src={digestGallery.photos[digestGallery.currentIndex]?.url}
                    alt={
                      digestGallery.photos[digestGallery.currentIndex]
                        ?.taskName || "Progress photo"
                    }
                    className="digest-gallery-image"
                  />
                </div>

                <div className="digest-gallery-caption">
                  <span className="digest-gallery-task-name">
                    {digestGallery.photos[digestGallery.currentIndex]
                      ?.taskName || "Progress Photo"}
                  </span>
                  <span className="digest-gallery-counter">
                    {digestGallery.currentIndex + 1} of{" "}
                    {digestGallery.photos.length}
                  </span>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </>
  );
}

export default ClientDashboard;
